import { State, Action, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { patch, updateItem } from '@ngxs/store/operators';
import { SupportService } from '@support/services/support.service';
import {
  CancelOrderAction,
  OrderBulkSimAction,
  OrderBulkSimSuccessAction,
  OrderBulkSimErrorAction,
  SupportResetAction,
  OrderHistorySearchV2Action,
  OrderHistorySearchV2SuccessAction,
  OrderHistorySearchErrorV2Action,
  MissingImeiCountAction,
  MissingImeiCountErrorAction,
  ManuallySetOrderStatusToCancelledAction,
  OrderHistoryAppendNewOrdersV2Action,
  OrderHistoryAppendNewOrdersV2SuccessAction,
  OrderHistoryAppendNewOrdersV2ErrorAction,
} from './support.actions';
import { OrderService } from '@sales/services/order.service';
import { Injectable, inject } from '@angular/core';
import {
  DetailedOrderStatus,
  OrderHistoryItemV2,
  OrderHistoryResponseV2,
  OrderHistoryStatusCategory,
  Status,
} from '@core/models/order-history.model';
import { NotificationService } from '@core/services/notification.service';
import { SimService } from '@sales/services/sim.service';

export interface SupportStateModel {
  orderHistoryV2: OrderHistoryResponseV2;
  missingImeiCount: number;
  isBulkSimOrderSent: boolean;
  isLoadingSimOrderSent: boolean;
  isLoadingOrderHistoryV2: boolean;
  isGettingMoreOrdersV2: boolean;
}

@State<SupportStateModel>({
  name: 'support',
  defaults: {
    orderHistoryV2: null,
    missingImeiCount: 0,
    isBulkSimOrderSent: false,
    isLoadingSimOrderSent: false,
    isLoadingOrderHistoryV2: false,
    isGettingMoreOrdersV2: false,
  },
})
@Injectable()
export class SupportState {
  private orderService = inject(OrderService);
  private supportService = inject(SupportService);
  private notificationService = inject(NotificationService);
  private simService = inject(SimService);

  @Action(OrderHistorySearchV2Action)
  orderHistorySearchV2({ dispatch, patchState }: StateContext<SupportStateModel>, action: OrderHistorySearchV2Action) {
    patchState({
      isLoadingOrderHistoryV2: true,
    });
    return this.supportService.orderHistoryV2Search(action.searchRequest).pipe(
      tap(response => dispatch(new OrderHistorySearchV2SuccessAction(response))),
      catchError(() => dispatch(new OrderHistorySearchErrorV2Action('Henting av ordre feilet')))
    );
  }

  @Action(OrderHistorySearchV2SuccessAction)
  orderHistorySearchV2Success({ patchState }: StateContext<SupportStateModel>, action: OrderHistorySearchV2SuccessAction) {
    patchState({
      orderHistoryV2: action.searchResponse,
      isLoadingOrderHistoryV2: false,
    });
  }

  @Action(OrderHistorySearchErrorV2Action)
  orderHistorySearchErrorV2Action({ patchState }: StateContext<SupportStateModel>, action: OrderHistorySearchErrorV2Action) {
    this.notificationService.error(action.error);
    patchState({
      isLoadingOrderHistoryV2: false,
    });
  }

  @Action(OrderHistoryAppendNewOrdersV2Action)
  orderHistoryAppendNewOrdersV2Action(
    { dispatch, getState, patchState }: StateContext<SupportStateModel>,
    action: OrderHistoryAppendNewOrdersV2Action
  ) {
    const state = getState().orderHistoryV2;

    const searchRequest = action.searchRequest;
    searchRequest.currentPage = state.currentPage + 1;
    searchRequest.take = state.take;

    patchState({
      isGettingMoreOrdersV2: true,
    });

    return this.supportService.orderHistoryV2Search(searchRequest).pipe(
      tap(response => dispatch(new OrderHistoryAppendNewOrdersV2SuccessAction(response))),
      catchError(() => dispatch(new OrderHistoryAppendNewOrdersV2ErrorAction('Kunne ikke hente mere data')))
    );
  }

  @Action(OrderHistoryAppendNewOrdersV2ErrorAction)
  orderHistoryAppendNewOrdersV2ErrorAction(
    { patchState }: StateContext<SupportStateModel>,
    { error }: OrderHistoryAppendNewOrdersV2ErrorAction
  ) {
    this.notificationService.error(error);
    patchState({
      isGettingMoreOrdersV2: false,
    });
  }

  @Action(OrderHistoryAppendNewOrdersV2SuccessAction)
  orderHistoryAppendNewOrdersV2SuccessAction(
    { getState, setState }: StateContext<SupportStateModel>,
    { response }: OrderHistoryAppendNewOrdersV2SuccessAction
  ) {
    setState(
      patch<SupportStateModel>({
        orderHistoryV2: patch<OrderHistoryResponseV2>({
          items: getState().orderHistoryV2.items.concat(response.items),
          currentPage: response.currentPage,
          take: response.take,
        }),
        isGettingMoreOrdersV2: false,
      })
    );
  }

  @Action(MissingImeiCountAction)
  missingImeiCount({ dispatch, patchState }: StateContext<SupportStateModel>, action: MissingImeiCountAction) {
    return this.supportService.missingImeiSearch(action.searchRequest).pipe(
      tap(response =>
        patchState({
          missingImeiCount: response,
        })
      ),
      catchError(() => dispatch(new MissingImeiCountErrorAction('Henting av antall ordre med manglende IMEI feilet')))
    );
  }

  @Action(MissingImeiCountErrorAction)
  missingImeiCountError(ctx: StateContext<SupportStateModel>, action: MissingImeiCountErrorAction) {
    this.notificationService.error(action.error);
  }

  @Action(CancelOrderAction)
  cancelOrder(ctx: StateContext<SupportStateModel>, action: CancelOrderAction) {
    return this.orderService.cancelOrder(action.orderServiceId, action.reason).pipe(
      tap(() => {
        if (ctx.getState().orderHistoryV2) {
          ctx.dispatch(new ManuallySetOrderStatusToCancelledAction(action.orderServiceId));
        }
      })
    );
  }

  // Only alters the state-value of the recently cancelled order.
  // Takes a little while before SPS returns the order as cancelled, hence the manual editing here in the state
  @Action(ManuallySetOrderStatusToCancelledAction)
  manuallySetOrderStatusToCancelled(
    { setState }: StateContext<SupportStateModel>,
    action: ManuallySetOrderStatusToCancelledAction
  ) {
    const cancelledStatus: Status = {
      statusCategory: OrderHistoryStatusCategory.Cancelled,
      detailedOrderStatuses: [DetailedOrderStatus.Cancelled],
    };

    setState(
      patch({
        orderHistoryV2: patch({
          items: updateItem<OrderHistoryItemV2>(
            item => item.orderServiceId === action.orderId,
            patch({ status: cancelledStatus, canBeCancelled: false })
          ),
        }),
      })
    );
  }

  @Action(OrderBulkSimAction)
  orderBulkSim(ctx: StateContext<SupportStateModel>, action: OrderBulkSimAction) {
    ctx.patchState({ isLoadingSimOrderSent: true });
    return this.simService.orderBulkSim(action.orderSimRequest).pipe(
      tap(() => ctx.dispatch(new OrderBulkSimSuccessAction())),
      catchError(() => ctx.dispatch(new OrderBulkSimErrorAction('Noe gikk galt under sim bestilling, prøv igjen senere.')))
    );
  }

  @Action(OrderBulkSimSuccessAction)
  orderBulkSimSuccessAction(ctx: StateContext<SupportStateModel>) {
    ctx.patchState({ isBulkSimOrderSent: true, isLoadingSimOrderSent: false });
  }

  @Action(OrderBulkSimErrorAction)
  orderBulkSimError(ctx: StateContext<SupportStateModel>, { error }: OrderBulkSimErrorAction) {
    this.notificationService.error(error);
    ctx.patchState({ isLoadingSimOrderSent: false });
  }

  @Action(SupportResetAction)
  supportReset({ patchState }: StateContext<SupportStateModel>) {
    patchState({
      isBulkSimOrderSent: false,
      isLoadingSimOrderSent: false,
    });
  }
}
